import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AnalyticsService } from '../../services/analytics.service';
import {
  AnalyticsActionTypes,
  LoadAnalytics,
  LoadBidAnalytics,
  LoadAnalyticsFail,
  LoadAnalyticsSuccess,
  LoadBidAnalyticsFail,
  LoadBidAnalyticsSuccess
} from './actions';
import { DealAnalytics } from '../../types/analytics.types';

export type LoadAnalyticsResult = LoadAnalyticsSuccess | LoadAnalyticsFail;
export type LoadBidAnalyticsResult =
  | LoadBidAnalyticsSuccess
  | LoadBidAnalyticsFail;

@Injectable()
export class AnalyticsEffects {
  @Effect()
  public loadAnalytics$: Observable<LoadAnalyticsResult> = this.actions$.pipe(
    ofType<LoadAnalytics>(AnalyticsActionTypes.LoadAnalytics),
    switchMap((analytics) => this.getAnalytics(analytics))
  );

  @Effect()
  public loadBidAnalytics$: Observable<LoadBidAnalyticsResult> = this.actions$.pipe(
    ofType<LoadBidAnalytics>(AnalyticsActionTypes.LoadBidAnalytics),
    switchMap((analytics) => this.getBidAnalytics(analytics))
  );

  constructor(
    private actions$: Actions,
    private analyticsService: AnalyticsService
  ) {}

  private getAnalytics(action: LoadAnalytics): Observable<LoadAnalyticsResult> {
    return this.analyticsService
      .getAnalytics(action.payload.dealId, action.payload.overrides)
      .pipe(
        map((result) => {
          return this.onGetAnalyticsSuccess(result);
        }),
        catchError(this.onGetAnalyticsFail.bind(this))
      );
  }
  private getBidAnalytics(
    action: LoadBidAnalytics
  ): Observable<LoadBidAnalyticsResult> {
    return this.analyticsService
      .getBidAnalytics(action.payload.dealId, action.payload.overrides)
      .pipe(
        map((result) => {
          return this.onGetBidAnalyticsSuccess(result);
        }),
        catchError(this.onGetBidAnalyticsFail.bind(this))
      );
  }

  private onGetAnalyticsFail(httpError: HttpErrorResponse) {
    const error = httpError && httpError.message;
    const errorCode = httpError.status;
    return of(new LoadAnalyticsFail({ error, errorCode }));
  }

  private onGetAnalyticsSuccess(result: DealAnalytics): LoadAnalyticsSuccess {
    return new LoadAnalyticsSuccess({ data: result });
  }
  private onGetBidAnalyticsFail(httpError: HttpErrorResponse) {
    const error = httpError && httpError.message;
    const errorCode = httpError.status;
    return of(new LoadBidAnalyticsFail({ error, errorCode }));
  }

  private onGetBidAnalyticsSuccess(
    result: DealAnalytics
  ): LoadBidAnalyticsSuccess {
    return new LoadBidAnalyticsSuccess({ data: result });
  }
}
